import { isAllowedFileType } from '@shared/all/uploadTypes';
import { fileTypeFromBuffer } from 'file-type/core';

import type { UploadType } from './types';

/**
 * OPFS blob storage service for private posts
 * Stores blobs as raw files in the Origin Private File System
 * Each user has their own blob directory to ensure privacy isolation
 *
 * Storage layout:
 *   OPFS root/
 *     factiii_blobs_{userId}/
 *       blob-abc-def.jpg      # raw binary data (extension encodes MIME type)
 */

/** Detect MIME from raw bytes, returns fallback for unknown types */
const detectMime = async (buffer: ArrayBuffer): Promise<string> => {
  const fileType = await fileTypeFromBuffer(new Uint8Array(buffer));
  return fileType?.mime || 'application/octet-stream';
};

type DirectoryEntries = AsyncIterable<[string, FileSystemHandle]>;

const getBlobDbName = (userId: number): string => {
  return `factiii_blobs_${userId}`;
};

class BlobStorageService {
  private dir: FileSystemDirectoryHandle | null = null;
  private activeUrls = new Map<string, string>(); // Track active blob URLs by key
  private userId: number;

  constructor(userId: number) {
    this.userId = userId;
  }

  async init(): Promise<void> {
    const root = await navigator.storage.getDirectory();
    this.dir = await root.getDirectoryHandle(getBlobDbName(this.userId), {
      create: true
    });
  }

  private ensureInit(): FileSystemDirectoryHandle {
    if (!this.dir) {
      throw new Error('BlobStorage not initialized');
    }
    return this.dir;
  }

  private async inferMimeType(buffer: ArrayBuffer) {
    const fileType = await fileTypeFromBuffer(new Uint8Array(buffer));

    if (!fileType || !isAllowedFileType(fileType.mime, fileType.ext)) {
      throw new Error(
        'Unsupported file type. Please provide an image, video, PDF, docx, xlsx, or pptx.'
      );
    }

    return { mime: fileType.mime, ext: fileType.ext };
  }

  private async writeFile(key: string, data: ArrayBuffer): Promise<void> {
    const dir = this.ensureInit();

    const fileHandle = await dir.getFileHandle(key, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(data);
    await writable.close();
  }

  async storeFile({
    uri,
    base64Data
  }: {
    uri?: string;
    base64Data?: string;
  }): Promise<{ key: string; mimeType: UploadType }> {
    let blobData: ArrayBuffer;

    if (uri) {
      const response = await fetch(uri);
      blobData = await response.arrayBuffer();
    } else if (base64Data) {
      const raw = base64Data.replace(/^data:[^;]+;base64,/, '');
      const binaryString = atob(raw);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      blobData = bytes.buffer as ArrayBuffer;
    } else {
      throw new Error('No data provided');
    }

    const { mime, ext } = await this.inferMimeType(blobData);

    const key = `blob-${Date.now()}-${Math.random().toString(36).substring(2, 11)}.${ext}`;
    await this.writeFile(key, blobData);

    return {
      key,
      mimeType: mime.includes('image')
        ? 'IMAGE'
        : mime.includes('video')
          ? 'VIDEO'
          : 'DOCUMENT'
    };
  }

  async getUrl(key: string): Promise<string | null> {
    // Return existing URL if already created
    if (this.activeUrls.has(key)) {
      return this.activeUrls.get(key) || null;
    }

    const dir = this.ensureInit();

    try {
      const fileHandle = await dir.getFileHandle(key);
      const file = await fileHandle.getFile();
      const buffer = await file.arrayBuffer();
      const mimeType = await detectMime(buffer);
      const blob = new Blob([buffer], { type: mimeType });

      const url = URL.createObjectURL(blob);
      this.activeUrls.set(key, url);
      return url;
    } catch {
      return null;
    }
  }

  /**
   * Revoke a blob URL to free memory
   * Should be called when the URL is no longer needed
   */
  revokeUrl(key: string): void {
    const url = this.activeUrls.get(key);
    if (url) {
      URL.revokeObjectURL(url);
      this.activeUrls.delete(key);
    }
  }

  /**
   * Revoke all active blob URLs
   * Useful for cleanup on component unmount or error scenarios
   */
  revokeAllUrls(): void {
    for (const url of this.activeUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.activeUrls.clear();
  }

  /**
   * Get blob data as base64 for syncing to other devices
   */
  async getBlobData(key: string): Promise<string | null> {
    const dir = this.ensureInit();

    try {
      const fileHandle = await dir.getFileHandle(key);
      const file = await fileHandle.getFile();
      const buffer = await file.arrayBuffer();

      const mimeType = await detectMime(buffer);

      // Convert ArrayBuffer to base64 data URL
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      return `data:${mimeType};base64,${base64}`;
    } catch {
      return null;
    }
  }

  /**
   * Store blob data from base64 (for syncing from other devices)
   */
  async storeBlobFromBase64(key: string, base64Data: string): Promise<void> {
    // Parse the data URL to extract mime type and base64 data
    const dataUrlMatch = base64Data.match(/^data:([^;]+);base64,(.+)$/);

    if (!dataUrlMatch) {
      throw new Error('Invalid base64 data URL format');
    }

    const base64 = dataUrlMatch[2];

    // Convert base64 to Uint8Array
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    await this.writeFile(key, bytes.buffer as ArrayBuffer);
  }

  /**
   * List all stored blobs (metadata only, not the actual blob data)
   */
  async listAll(): Promise<
    Array<{ id: string; mimeType: string; size: number; createdAt: Date }>
  > {
    const dir = this.ensureInit();
    const blobs: Array<{
      id: string;
      mimeType: string;
      size: number;
      createdAt: Date;
    }> = [];

    for await (const [name] of dir as unknown as DirectoryEntries) {
      try {
        const fileHandle = await dir.getFileHandle(name);
        const file = await fileHandle.getFile();
        const buffer = await file.arrayBuffer();
        blobs.push({
          id: name,
          mimeType: await detectMime(buffer),
          size: file.size,
          createdAt: new Date(file.lastModified)
        });
      } catch {
        // Skip files we can't read
      }
    }

    // Sort by createdAt descending (newest first)
    blobs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    return blobs;
  }

  /**
   * Get total storage used by all blobs
   */
  async getTotalSize(): Promise<{ count: number; totalBytes: number }> {
    const blobs = await this.listAll();
    const totalBytes = blobs.reduce((sum, blob) => sum + blob.size, 0);
    return { count: blobs.length, totalBytes };
  }

  /**
   * Get all blob keys (just the IDs, not the full blob data)
   */
  async getAllKeys(): Promise<string[]> {
    const dir = this.ensureInit();
    const keys: string[] = [];

    for await (const [name] of dir as unknown as DirectoryEntries) {
      keys.push(name);
    }

    return keys;
  }

  /**
   * Delete multiple blobs at once
   */
  async deleteMany(
    keys: string[]
  ): Promise<{ deleted: number; failed: string[] }> {
    let deleted = 0;
    const failed: string[] = [];

    for (const key of keys) {
      try {
        await this.delete(key);
        deleted++;
      } catch {
        failed.push(key);
      }
    }

    return { deleted, failed };
  }

  async delete(key: string): Promise<boolean> {
    const dir = this.ensureInit();

    try {
      await dir.removeEntry(key);
    } catch {
      // File may not exist — that's fine
    }

    // Revoke URL when blob is deleted
    this.revokeUrl(key);
    return true;
  }

  /**
   * Close the storage and revoke all URLs
   * Should be called when user logs out
   */
  close(): void {
    this.revokeAllUrls();
    this.dir = null;
  }
}

// Cache of blob storage instances per user
const instances = new Map<number, BlobStorageService>();
const initPromises = new Map<number, Promise<void>>();

/**
 * Get blob storage for a specific user
 * Each user has their own isolated blob directory
 */
export const getBlobStorage = async (
  userId: number
): Promise<BlobStorageService> => {
  if (!instances.has(userId)) {
    const instance = new BlobStorageService(userId);
    instances.set(userId, instance);
    initPromises.set(userId, instance.init());
  }
  await initPromises.get(userId);
  return instances.get(userId)!;
};

/**
 * Close and remove blob storage for a user
 * Should be called when user logs out
 */
export const closeBlobStorage = (userId: number): void => {
  const instance = instances.get(userId);
  if (instance) {
    instance.close();
    instances.delete(userId);
    initPromises.delete(userId);
  }
};

/**
 * Get the directory name for a user's blob storage
 * Useful for displaying in debug UI
 */
export { getBlobDbName };

export default BlobStorageService;
